/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { getResource } from "../language/resources/getResource";
import { COLUMN_SPACING, ROW_SPACING } from "../forms/styles";
import ModalView from "../layout/modal/ModalView";
import ModalTitleHeader from "../layout/modal/ModalTitleHeader";
import deleteIcon from "../assets/icons/trashcan.svg";
import { Row } from "../layout/flexbox";
import * as Buttons from "../forms/elements/buttons";
import { Input } from "../forms/elements/Input";
import LabelGroup from "../forms/elements/LabelGroup";
import { StyledBoxedMessage } from "../forms/elements/boxedMessage/StyledBoxedMessage";
import { BoxedErrorInverse } from "../forms/elements/boxedMessage/boxedMessageStyles";
import { openPopout, Popout } from "../layout/window/openPopout";
import { PopoutPortal } from "../layout/window/PopoutPortal";
import { PopoutBlocker } from "../layout/window/PopoutBlocker";
import { popoutOpenSize } from "../layout/window/popoutOpenSize";
import { useDockedSize } from "../layout/window/useDockedSize";
import { usePopoutFit } from "../layout/window/usePopoutFit";
import { showSnackbarAction } from "../snackbar/snackbarAction";
import { LiftAccessRuleSet } from "./accessRules/liftAccessRuleSet/models";
import {
  BulkDeleteSummary,
  SUMMARY_GRID_WIDTH,
} from "./accessRules/liftAccessRuleSet/BulkDeleteSummary";
import {
  bulkDeleteConfirmPhrase,
  isBulkDeleteConfirmed,
} from "./accessRules/liftAccessRuleSet/bulkDeleteConfirm";
import { GRID_HEIGHT_BUDGET } from "./accessRules/liftAccessRuleSet/bulkEditChangeScan";
import { useSearchResultFreshness } from "./useSearchResultFreshness";
import failureIcon from "../assets/icons/failureTriangleRed.svg";
import {
  BLUE_CREEK,
  COOL_BLUE,
  IKON_BLUE,
  RED_CREEK,
  WHITE,
} from "../styles/colors";
import { MediumText, SmallText } from "../styles/styles";

type Props = {
  ruleSets: LiftAccessRuleSet[];
  /** Starts the delete. The caller closes this dialog. */
  onConfirm: () => void;
  onDismiss: () => void;
};

// Content padding either side, plus room for the vertical scrollbar.
const CONTENT_CHROME = 72;

// Floor, matching MODAL_SIZE.LARGE: the identity columns alone don't need this
// much, but the warning and the type-to-confirm row read as cramped below it.
const MIN_MODAL_WIDTH = 1000;

const modalWidthPx = (showAllColumns: boolean) =>
  Math.max(
    MIN_MODAL_WIDTH,
    (showAllColumns ? SUMMARY_GRID_WIDTH.all : SUMMARY_GRID_WIDTH.identity) +
      CONTENT_CHROME,
  );

const gridWidth = (showAllColumns: boolean) =>
  `min(${modalWidthPx(showAllColumns)}px, 96vw)`;

// The dialog around the grid: title, warning, confirm row and buttons. Biased high —
// undershooting opens the window already scrolled.
const CHROME_HEIGHT = 460;

// usePopoutFit remembers sizes per step; this dialog has one.
const POPOUT_STEP = "confirm";

/**
 * The window height to open at and to fit to. The grid is budgeted, so a long selection
 * scrolls rather than the window growing.
 *
 * `collapsedHeight` is the modal without the grid — the height closing it comes back
 * to. Read at pop-out time it would catch an expanded modal instead. 0 until the first
 * reading, which leaves the opening size to the docked box.
 */
export const fitHeightPx = (
  gridOpen: boolean,
  gridHeightPx: number,
  collapsedHeight: number,
) =>
  gridOpen
    ? CHROME_HEIGHT + Math.min(gridHeightPx, GRID_HEIGHT_BUDGET)
    : collapsedHeight;

const headerCss = css`
  position: relative;
  flex: 0 0 auto;
`;

// Pop out / Dock. Kept identical to the bulk edit dialog's corner action.
const cornerButtonCss = css`
  position: absolute;
  top: 22px;
  left: 20px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 13px;
  border: 1px solid ${BLUE_CREEK};
  border-radius: 4px;
  background: ${WHITE};
  color: ${BLUE_CREEK};
  cursor: pointer;

  &:hover {
    background: ${COOL_BLUE};
  }
`;

// Stands in for Modal's max-height. height, not min-height: the column has to be
// bounded by the window, or it grows past the buttons instead of the grid scrolling.
const popoutRootCss = css`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  background: ${WHITE};
`;

// Everything keeps its natural height except the child marked `.fill`, which takes
// what's left — so the grid scrolls and the dialog itself never does.
const fillableColumnCss = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;

  > * {
    flex: 0 0 auto;
  }

  /* min-height: 0 is what lets this child hand its height to the grid. */
  > .fill {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const contentCss = css`
  padding: 0 28px 28px;
  box-sizing: border-box;
  ${COLUMN_SPACING};
  ${fillableColumnCss};
`;

const questionCss = css`
  font-size: 15px;
  color: ${IKON_BLUE};
`;

const confirmSectionCss = css`
  margin-top: 12px;
`;

const invalidInputCss = css`
  border-color: ${RED_CREEK};

  &:focus {
    outline-color: ${RED_CREEK};
  }
`;

// In the footer, not a banner above the grid. Plain text rather than a bordered box:
// it is not clickable.
const staleWarningCss = css`
  display: flex;
  align-items: center;
  margin-right: auto;
  max-width: 60%;
  gap: 8px;
  ${MediumText};
  font-weight: 600;
  color: ${RED_CREEK};

  img {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }
`;

// LabelGroup joins label and sub-label with a single space, too tight under a bold
// label — so restate its sub-label styling with real spacing.
const confirmHintCss = css`
  font-weight: normal;
  ${SmallText};
  margin-left: 10px;
`;

const mismatchLabelCss = css`
  ${confirmHintCss};
  color: ${RED_CREEK};
`;

export const AccessMultiDeleteModal = (props: Props) => {
  const dispatch = useDispatch();
  const resources = useSelector((state: IAppState) => state.locale);
  const title = getResource(resources, "ACCESS_RULE_DELETE_ACCESS_RULES");

  // Held here, not in the body, so all three survive the body remounting as it moves
  // between the docked modal and the window.
  const [confirmText, setConfirmText] = useState("");
  const [gridOpen, setGridOpen] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const [gridHeightPx, setGridHeightPx] = useState(0);

  // The modal box with the grid closed — the dialog's height without it.
  const [collapsedHeight, setCollapsedHeight] = useState(0);

  const [popout, setPopout] = useState<Popout | null>(null);

  // Must match what popoutOpenSize is given, so compute it once.
  const fitHeight = fitHeightPx(gridOpen, gridHeightPx, collapsedHeight);

  const { rememberedSize, noteOpened } = usePopoutFit({
    popout,
    step: POPOUT_STEP,
    targetWidthPx: modalWidthPx(showAllColumns),
    // Without this the grid unfolds into whatever slack is left and gridSectionCss clips it.
    targetHeightPx: fitHeight,
  });

  const { dockedRef, dockedSize } = useDockedSize<HTMLDivElement>();

  // The grid starts closed, so there is always a reading by the time one is needed.
  useLayoutEffect(() => {
    if (popout || gridOpen) return;
    const docked = dockedSize();
    if (docked) setCollapsedHeight(docked.height);
  }, [popout, gridOpen, dockedSize]);

  const handlePopOut = useCallback(() => {
    const remembered = rememberedSize();

    const size = popoutOpenSize({
      remembered,
      docked: dockedSize(),
      wantedWidth: modalWidthPx(showAllColumns),
      fitHeight,
      // Nothing docked and never popped out: the collapsed dialog is chrome only.
      wantedHeight: fitHeight || CHROME_HEIGHT,
    });

    const opened = openPopout({
      title,
      width: size.width,
      height: size.height,
    });
    if (!opened) {
      dispatch(
        showSnackbarAction(
          getResource(resources, "ACCESS_RULE_BULK_EDIT_POPUP_BLOCKED"),
        ),
      );
      return;
    }
    // What the opened size was chosen for, so a stale one stays detectable.
    noteOpened(
      remembered?.fitsWidthPx ?? size.width,
      remembered?.fitsHeightPx ?? fitHeight,
    );
    setPopout(opened);
  }, [
    dispatch,
    resources,
    title,
    fitHeight,
    showAllColumns,
    rememberedSize,
    noteOpened,
    dockedSize,
  ]);

  // Both docking and the window closing drop the handle; unmounting PopoutPortal closes
  // the window. No run-in-progress guard needed: confirming hands the run to the page,
  // which closes this dialog as it starts, so no window outlives a live delete.
  const handleDock = useCallback(() => setPopout(null), []);

  // Read from the store, not props: showModalAction freezes props at open time, and
  // this also has to work once the dialog is popped out into its own window.
  const { resultsStale } = useSearchResultFreshness();

  const confirmPhrase = bulkDeleteConfirmPhrase(resources);
  const confirmed = isBulkDeleteConfirmed(confirmText, confirmPhrase);
  const mismatch = confirmText.length > 0 && !confirmed;

  const body = (
    <>
      <StyledBoxedMessage
        message={getResource(resources, "ACCESS_RULE_DATA_CHANGE_WARNING")}
        boxStyle={BoxedErrorInverse}
      />
      <div css={questionCss}>
        {getResource(resources, "ACCESS_RULE_BULK_DELETE_CONFIRMATION")}
      </div>
      {/* Only flexed while the grid is showing; collapsed it sits at its natural
          height rather than pushing the confirm row down. */}
      <div className={gridOpen ? "fill" : undefined}>
        <BulkDeleteSummary
          ruleSets={props.ruleSets}
          gridOpen={gridOpen}
          onGridOpenChange={setGridOpen}
          showAllColumns={showAllColumns}
          onShowAllColumnsChange={setShowAllColumns}
          onGridHeightChange={setGridHeightPx}
        />
      </div>
      <LabelGroup
        rootCss={confirmSectionCss}
        label={getResource(resources, "ACCESS_RULE_BULK_DELETE_CONFIRM_LABEL")}
        subLabel={getResource(
          resources,
          mismatch
            ? "ACCESS_RULE_BULK_DELETE_CONFIRM_MISMATCH"
            : "ACCESS_RULE_BULK_DELETE_CONFIRM_HINT",
          confirmPhrase,
        )}
        subLabelCss={mismatch ? mismatchLabelCss : confirmHintCss}
      >
        <Input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={confirmPhrase}
          aria-invalid={mismatch}
          css={mismatch ? invalidInputCss : undefined}
        />
      </LabelGroup>
      <Row justifyContent="flex-end" rootCss={ROW_SPACING}>
        {/* Beside the button it disables, so the reason is where the click is. */}
        {resultsStale && (
          <span css={staleWarningCss}>
            <img src={failureIcon} alt="" aria-hidden="true" />
            {getResource(
              resources,
              "ACCESS_RULE_BULK_EDIT_RESULTS_STALE_SHORT",
            )}
          </span>
        )}
        <Buttons.BigSecondary
          type="button"
          title={getResource(resources, "ACCESS_RULE_MODAL_CANCEL_TITLE")}
          onClick={props.onDismiss}
        >
          {getResource(resources, "ACCESS_RULE_MODAL_CANCEL")}
        </Buttons.BigSecondary>
        <Buttons.BigNegative
          type="button"
          title={getResource(
            resources,
            "ACCESS_RULE_BULK_DELETE_CONFIRM_TITLE",
          )}
          disabled={!confirmed || resultsStale}
          onClick={props.onConfirm}
        >
          {getResource(resources, "ACCESS_RULE_DELETE")}
        </Buttons.BigNegative>
      </Row>
    </>
  );

  // Popped out: the dialog renders into the separate window, and the page it came from
  // gets the notice and the page lock instead of a modal over the list.
  if (popout) {
    return (
      <>
        <PopoutBlocker popout={popout} title={title} onDock={handleDock} />
        <PopoutPortal popout={popout} onClose={handleDock}>
          <div css={popoutRootCss}>
            <div css={headerCss}>
              <button
                type="button"
                css={cornerButtonCss}
                onClick={handleDock}
                title={getResource(resources, "ACCESS_RULE_BULK_EDIT_DOCK")}
              >
                {getResource(resources, "ACCESS_RULE_BULK_EDIT_DOCK")}
              </button>
              <ModalTitleHeader
                resources={resources}
                heading={title}
                icon={deleteIcon}
                onDismiss={props.onDismiss}
              />
            </div>
            <div css={contentCss}>{body}</div>
          </div>
        </PopoutPortal>
      </>
    );
  }

  return (
    <ModalView
      accessibilityModalLabel={title}
      onDismiss={props.onDismiss}
      // Type-to-confirm holds in-progress intent — don't let a stray Escape or
      // backdrop click drop it. Close is deliberate (✕, Cancel).
      disableClickoffDismissable
      disableKeypressDismissable
      // Sized to the grid, not the reverse: narrowing back down when the detail
      // columns hide keeps Description from absorbing the slack as dead space.
      size={gridWidth(showAllColumns)}
    >
      {/* First child of the modal box, which is what dockedSize measures. */}
      <div css={headerCss} ref={dockedRef}>
        <button
          type="button"
          css={cornerButtonCss}
          onClick={handlePopOut}
          title={getResource(resources, "ACCESS_RULE_BULK_EDIT_POP_OUT_TITLE")}
        >
          ⇗ {getResource(resources, "ACCESS_RULE_BULK_EDIT_POP_OUT")}
        </button>
        <ModalTitleHeader
          resources={resources}
          heading={title}
          icon={deleteIcon}
          onDismiss={props.onDismiss}
        />
      </div>
      <div css={contentCss}>{body}</div>
    </ModalView>
  );
};
